import { useElementSize } from "@vueuse/core"
import debounce from "lodash-es/debounce"

const PADDING_FACTOR = 0.2

export const useVisibleAssets = <T extends { $el?: Element | null }>(
  mapReference: Ref<T | null>,
  map: Ref<google.maps.Map | undefined>,
  allAssets: Ref<Array<MapAsset>>,
  clusterCutoffLimit: Ref<number>,
  currentZoom: Ref<number>,
) => {
  const currentBounds = ref<MapBounds>({
    north: 0,
    west: 0,
    south: 0,
    east: 0,
  })

  const mapElement = computed<HTMLElement | null>(() => (mapReference.value?.$el as HTMLElement | null) ?? null)
  const mapSize = useElementSize(mapElement)

  // Get the long anf lat diff of 10% above the cutoff rate
  const getClusterCutoffMapDifferance = () => {
    // Get the center of the map because due to map projection making latitude sizes per pixel different per location
    const mapCenterLatitude = map.value?.getCenter()?.lat() ?? 0

    // https://stackoverflow.com/questions/47106276/converting-pixels-to-latlng-coordinates-from-google-static-image
    const parallelMultiplier = Math.cos((mapCenterLatitude * Math.PI) / 180)
    const degreesPerPixelX = 360 / Math.pow(2, clusterCutoffLimit.value + 8)
    const degreesPerPixelY = (360 / Math.pow(2, clusterCutoffLimit.value + 8)) * parallelMultiplier

    const trueLatDiff = degreesPerPixelY * mapSize.height.value
    const trueLngDiff = degreesPerPixelX * mapSize.width.value

    // Add 10% to the max limit to take care of any errors
    return {
      latDiff: trueLatDiff * 1.1,
      lngDiff: trueLngDiff * 1.1,
    }
  }

  const clusterBounds = computed((): MapBounds => {
    const clusterCutoffMapDifferance = getClusterCutoffMapDifferance()

    const latDiff = Math.min(clusterCutoffMapDifferance.latDiff, currentBounds.value.north - currentBounds.value.south)
    const lngDiff = Math.min(clusterCutoffMapDifferance.lngDiff, currentBounds.value.east - currentBounds.value.west)

    const latBoundsPadding = latDiff * PADDING_FACTOR
    const lngBoundsPadding = lngDiff * PADDING_FACTOR

    return {
      north: currentBounds.value.north + latBoundsPadding,
      south: currentBounds.value.north - latDiff - latBoundsPadding,
      east: currentBounds.value.east + lngBoundsPadding,
      west: currentBounds.value.east - lngDiff - lngBoundsPadding,
    }
  })

  const isWithinBounds = (asset: MapAsset, localBounds: MapBounds) => {
    const [lng, lat] = asset.location.gps!.coordinates

    return lat <= localBounds.north && lat >= localBounds.south && lng <= localBounds.east && lng >= localBounds.west
  }

  const visibleAssets = ref<Array<MapAsset>>([])

  const setVisibleAssets = debounce((assetsToSet: Array<MapAsset>, boundsToCheck: MapBounds) => {
    visibleAssets.value = assetsToSet.filter((asset) => isWithinBounds(asset, boundsToCheck))
  }, 100)

  watchEffect(() => {
    if (currentZoom.value < clusterCutoffLimit.value) {
      setVisibleAssets.cancel()
      visibleAssets.value = []
      return
    }

    setVisibleAssets(allAssets.value, clusterBounds.value)
  })

  const initializeBoundListener = () => {
    if (!map.value) {
      throw new Error("Map is not initialized")
    }

    map.value.addListener("bounds_changed", () => {
      const bounds = map.value?.getBounds()

      if (!bounds) {
        return
      }

      const northEast = bounds.getNorthEast()
      const southWest = bounds.getSouthWest()

      currentBounds.value = {
        north: northEast.lat(),
        east: northEast.lng(),
        south: southWest.lat(),
        west: southWest.lng(),
      }
    })
  }

  return {
    visibleAssets,
    initializeBoundListener,
  }
}
